import axios from 'axios';

import { AiCredential } from '../../entities/ai-credential';
import { CodeAgentProvider } from '../../entities/code-agent-task';
import { auth } from '../firebase/firebase';
import { API_BASE_URL } from './index';

const getHeaders = async () => {
  const idToken = await auth.currentUser?.getIdToken();
  return { Authorization: `Bearer ${idToken}` };
};

export const aiCredentialErrorMessage = (err: unknown): string | undefined =>
  axios.isAxiosError(err) ? (err.response?.data as { message?: string } | undefined)?.message : undefined;

export const listAiCredentials = async (): Promise<AiCredential[]> => {
  const headers = await getHeaders();
  const { data } = await axios.get<{ credentials: AiCredential[] }>(`${API_BASE_URL}/api/ai-credentials`, {
    headers,
  });
  return data.credentials;
};

export const saveAiCredential = async (
  provider: CodeAgentProvider,
  apiKey: string,
): Promise<{ success: boolean; verified: boolean }> => {
  const headers = await getHeaders();
  const { data } = await axios.post<{ success: boolean; verified: boolean }>(
    `${API_BASE_URL}/api/ai-credentials`,
    { provider, apiKey },
    { headers },
  );
  return data;
};

export const deleteAiCredential = async (provider: CodeAgentProvider): Promise<void> => {
  const headers = await getHeaders();
  await axios.delete(`${API_BASE_URL}/api/ai-credentials/${provider}`, { headers });
};

// MF-42 — persisted per-user default BYOK provider for the system agents (today: the Standalone
// agent; forwarding is harmless to the others so they pick this up for free once wired). The BE
// only lets this be set to a provider the user already has a saved credential for — deleting that
// credential clears the default server-side too.
export const getDefaultAiProvider = async (): Promise<CodeAgentProvider | null> => {
  const headers = await getHeaders();
  const { data } = await axios.get<{ provider: CodeAgentProvider | null }>(
    `${API_BASE_URL}/api/ai-credentials/default`,
    { headers },
  );
  return data.provider ?? null;
};

// `provider: null` clears the default (falls back to mFlow-hosted).
export const setDefaultAiProvider = async (provider: CodeAgentProvider | null): Promise<void> => {
  const headers = await getHeaders();
  await axios.post(`${API_BASE_URL}/api/ai-credentials/default`, { provider }, { headers });
};
